import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import { Field, Input, Select, SaveMessage, Card, CardHeader, SubmitBtn, SecondaryBtn, EmptyState, Badge, ENTITY_LABELS } from './Shared';
import { ReviewRequests } from './Participation';
import { qboFetch } from './qbo';

const money = n => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const today = () => new Date().toLocaleDateString('en-CA');
const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const monthLabel = d => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const confirmDateFor = period => { const d = new Date(period + 'T00:00:00'); d.setMonth(d.getMonth() + 1); d.setDate(1); return d.toLocaleDateString('en-CA'); };
const STATUS_VARIANT = { Paid: 'green', Partial: 'amber', Open: 'blue', Overdue: 'red' };
const TABS = [['monthly', 'Monthly cash'], ['invoices', 'Royalty invoices'], ['participation', 'Wesley participation'], ['founders', "Founder's royalty"], ['reviews', 'Review requests']];

export default function RoyaltiesPage({ profile }) {
  const [tab, setTab] = useState('monthly');
  const [rates, setRates] = useState({});
  const [months, setMonths] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [accruals, setAccruals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [partPays, setPartPays] = useState([]);
  const [founders, setFounders] = useState([]);
  const [syncState, setSyncState] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [msg, setMsg] = useState({ show: false, text: '', error: false });
  const flash = (text, error = false) => { setMsg({ show: true, text, error }); if (!error) setTimeout(() => setMsg({ show: false }), 4000); };

  const load = useCallback(async () => {
    const q = await Promise.all([
      supabase.from('royalty_entities').select('*'),
      supabase.from('royalty_months').select('*').order('period', { ascending: false }),
      supabase.from('royalty_invoice_status').select('*').order('invoice_date', { ascending: false }).order('invoice_no'),
      supabase.from('participation_accruals').select('*').order('received_date', { ascending: false }),
      supabase.from('participation_summary').select('*').maybeSingle(),
      supabase.from('participation_payments').select('*').order('paid_date', { ascending: false }),
      supabase.from('founders_royalty_quarterly').select('*').order('quarter_start', { ascending: false }),
      supabase.from('qbo_cash_sync_state').select('*')
    ]);
    const err = q.find(r => r.error);
    setLoadError(err ? err.error.message : '');
    setRates(Object.fromEntries((q[0].data || []).map(r => [r.entity, r])));
    setMonths(q[1].data || []); setInvoices(q[2].data || []); setAccruals(q[3].data || []);
    setSummary(q[4].data || null); setPartPays(q[5].data || []); setFounders(q[6].data || []); setSyncState(q[7].data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const owedToAO = invoices.reduce((s, i) => s + Number(i.balance || 0), 0);
  const overdue = invoices.filter(i => i.status === 'Overdue').reduce((s, i) => s + Number(i.balance || 0), 0);
  const draftCount = months.filter(m => m.status === 'draft').length;

  return (
    <div>
      <div style={{ fontSize: '13px', color: '#64748B', marginBottom: '16px', lineHeight: '1.6' }}>
        Cash collected in each month is snapshotted from QuickBooks once the month closes. Review and confirm it by the 10th; the 6% royalty invoice is
        issued on confirmation, emailed to the entity's recipients from royalties@ao-group.us, and is due the 15th. Royalties start with October 2026
        (Fire Watch NJ has paid since inception). Admin only.
      </div>

      {loadError && (
        <Card><div style={{ color: '#B91C1C', fontSize: '13px' }}>
          Could not load royalty data: {loadError}. If this says a table does not exist, run migrations/003_qbo_cash_and_royalties.sql in Supabase.
        </div></Card>
      )}

      <div className="metrics-grid">
        <div className="metric-card"><div className="metric-label">Royalty owed to AO</div><div className="metric-value blue">{money(owedToAO)}</div><div className="metric-sub">open invoices</div></div>
        <div className="metric-card"><div className="metric-label">Overdue</div><div className={'metric-value ' + (overdue > 0 ? 'red' : 'green')}>{money(overdue)}</div><div className="metric-sub">past due date</div></div>
        <div className="metric-card"><div className="metric-label">Awaiting your confirmation</div><div className={'metric-value ' + (draftCount > 0 ? 'amber' : 'green')}>{draftCount}</div><div className="metric-sub">entity-months in draft</div></div>
        <div className="metric-card"><div className="metric-label">Owed to Wesley (participation)</div><div className="metric-value blue">{money(summary && summary.outstanding)}</div><div className="metric-sub">accrued, unpaid</div></div>
      </div>

      <div style={{ display: 'flex', gap: '8px', margin: '4px 0 14px', flexWrap: 'wrap' }}>
        {TABS.map(([id, label]) => (
          <button key={id} className={'btn btn-sm ' + (tab === id ? 'btn-primary' : 'btn-secondary')} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      <SaveMessage {...msg} />

      {tab === 'monthly' && <MonthlyTab months={months} rates={rates} syncState={syncState} profile={profile} reload={load} flash={flash} />}
      {tab === 'invoices' && <InvoicesTab invoices={invoices} profile={profile} reload={load} flash={flash} />}
      {tab === 'participation' && <ParticipationTab accruals={accruals} summary={summary} payments={partPays} profile={profile} reload={load} flash={flash} />}
      {tab === 'founders' && <FoundersTab rows={founders} />}
      {tab === 'reviews' && <ReviewRequests profile={profile} mode="admin" />}
    </div>
  );
}

// ---------------------------------------------------------------- Monthly
function MonthlyTab({ months, rates, syncState, profile, reload, flash }) {
  const [busy, setBusy] = useState('');
  const [edit, setEdit] = useState({});          // month id -> { amount, reason }
  const [reviewing, setReviewing] = useState(null); // { entity, period }
  const periods = [...new Set(months.map(m => m.period))];
  const stale = Object.keys(ENTITY_LABELS).filter(e => !syncState.find(s => s.entity === e && s.last_full_sync_at));

  async function confirm(m) {
    const rate = rates[m.entity] ? Number(rates[m.entity].royalty_rate) : 0.06;
    const amt = Math.round(Number(m.cash_effective) * rate * 100) / 100;
    if (!window.confirm(`Confirm ${ENTITY_LABELS[m.entity]} ${monthLabel(m.period)}?\n\nCash collected ${money(m.cash_effective)} → royalty ${money(amt)}. This cannot be undone.`)) return;
    setBusy(m.id);
    const { data, error } = await supabase.rpc('royalty_confirm', { p_month_id: m.id });
    setBusy('');
    if (error) return flash(error.message, true);
    if (data && data.invoice_id) qboFetch('/royalty/send', { method: 'POST' }).catch(() => {});
    flash(data && data.invoice_id ? `Confirmed. Invoice issued: ${money(data.amount)}, due ${fmtDate(data.due_date)}.` : 'Confirmed. No royalty due (no cash collected).');
    reload();
  }
  async function refresh(period) {
    setBusy('r' + period);
    const { error } = await supabase.rpc('royalty_snapshot_month', { p_period: period, p_refresh: true });
    setBusy('');
    if (error) return flash(error.message, true);
    flash('Draft months refreshed from QuickBooks data.'); reload();
  }
  async function saveOverride(m) {
    const e = edit[m.id] || {};
    const amount = e.amount === '' || e.amount == null ? null : parseFloat(e.amount);
    if (amount !== null && (isNaN(amount) || amount < 0)) return flash('Enter a valid amount.', true);
    if (amount !== null && !(e.reason || '').trim()) return flash('A reason is required for an override.', true);
    const { error } = await supabase.from('royalty_months').update({ cash_override: amount, override_reason: amount === null ? null : e.reason.trim() }).eq('id', m.id);
    if (error) return flash(error.message, true);
    setEdit(x => { const n = { ...x }; delete n[m.id]; return n; });
    flash(amount === null ? 'Override cleared.' : 'Override saved.'); reload();
  }

  return (
    <div>
      {stale.length > 0 && (
        <Card><div style={{ fontSize: '13px', color: '#92400E' }}>
          QuickBooks cash has not synced yet for: {stale.map(e => ENTITY_LABELS[e]).join(', ')}. Month-end snapshots wait until every entity has synced after the month closes.
          Check the QuickBooks connection page.
        </div></Card>
      )}
      {!months.length && <Card><EmptyState text="No royalty months yet. Snapshots are created automatically after month-end once all five QuickBooks files have synced." /></Card>}
      {periods.map(period => {
        const rows = months.filter(m => m.period === period);
        const hasDraft = rows.some(m => m.status === 'draft');
        const canConfirm = today() >= confirmDateFor(period);
        return (
          <Card key={period}>
            <CardHeader title={monthLabel(period)} hint={hasDraft ? (canConfirm ? 'Ready to confirm' : 'Confirm on or after ' + fmtDate(confirmDateFor(period))) : 'All confirmed'}
              action={hasDraft ? <SecondaryBtn sm onClick={() => refresh(period)}>{busy === 'r' + period ? 'Refreshing…' : 'Refresh from QuickBooks'}</SecondaryBtn> : null} />
            <table className="data-table">
              <thead><tr><th>Entity</th><th style={{ textAlign: 'right' }}>Cash (QBO)</th><th style={{ textAlign: 'right' }}>Cash used</th><th style={{ textAlign: 'right' }}>Royalty</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {rows.sort((a, b) => a.entity.localeCompare(b.entity)).map(m => {
                  const rate = rates[m.entity] ? Number(rates[m.entity].royalty_rate) : 0.06;
                  const e = edit[m.id];
                  return (
                    <React.Fragment key={m.id}>
                      <tr>
                        <td>{ENTITY_LABELS[m.entity]}{m.legacy && <span style={{ color: '#94A3B8', fontSize: '11px' }}> · imported</span>}</td>
                        <td style={{ textAlign: 'right' }}>{m.legacy ? '—' : money(m.cash_qbo)}</td>
                        <td style={{ textAlign: 'right' }}>{money(m.cash_effective)}{m.cash_override != null && !m.legacy && <div style={{ fontSize: '11px', color: '#B45309' }}>override: {m.override_reason}</div>}</td>
                        <td style={{ textAlign: 'right' }}>{money(Math.round(Number(m.cash_effective) * rate * 100) / 100)}</td>
                        <td><Badge variant={m.status === 'confirmed' ? 'green' : 'amber'}>{m.status === 'confirmed' ? 'Confirmed' : 'Draft'}</Badge></td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {m.status === 'draft' && (<>
                            <button className="btn btn-secondary btn-sm" onClick={() => setReviewing(reviewing && reviewing.entity === m.entity && reviewing.period === m.period ? null : { entity: m.entity, period: m.period })}>Receipts</button>{' '}
                            <button className="btn btn-secondary btn-sm" onClick={() => setEdit(x => ({ ...x, [m.id]: { amount: m.cash_override ?? '', reason: m.override_reason || '' } }))}>Override</button>{' '}
                            <button className="btn btn-primary btn-sm" disabled={!canConfirm || busy === m.id} onClick={() => confirm(m)}>{busy === m.id ? '…' : 'Confirm'}</button>
                          </>)}
                        </td>
                      </tr>
                      {e && (
                        <tr><td colSpan={6}>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                            <div style={{ width: '160px' }}><Input type="number" value={e.amount} onChange={ev => setEdit(x => ({ ...x, [m.id]: { ...e, amount: ev.target.value } }))} placeholder="Cash amount (blank = clear)" /></div>
                            <div style={{ flex: 1, minWidth: '200px' }}><Input value={e.reason} onChange={ev => setEdit(x => ({ ...x, [m.id]: { ...e, reason: ev.target.value } }))} placeholder="Why (required) — e.g. deposit not recorded in QBO" /></div>
                            <button className="btn btn-primary btn-sm" onClick={() => saveOverride(m)}>Save</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setEdit(x => { const n = { ...x }; delete n[m.id]; return n; })}>Cancel</button>
                            
                          </div>
                        </td></tr>
                      )}
                      {reviewing && reviewing.entity === m.entity && reviewing.period === m.period && (
                        <tr><td colSpan={6}><ReceiptReview entity={m.entity} period={m.period} profile={profile} reload={reload} flash={flash} /></td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </Card>
        );
      })}
    </div>
  );
}

// Lists the QuickBooks receipts behind a month so non-revenue items can be excluded.
function ReceiptReview({ entity, period, profile, reload, flash }) {
  const [rows, setRows] = useState(null);
  const [reasonFor, setReasonFor] = useState(null);
  const [reason, setReason] = useState('');
  const load = useCallback(async () => {
    const d = new Date(period + 'T00:00:00'); d.setMonth(d.getMonth() + 1);
    const { data } = await supabase.from('qbo_cash_receipts').select('*').eq('entity', entity)
      .gte('txn_date', period).lt('txn_date', d.toLocaleDateString('en-CA')).order('txn_date');
    setRows(data || []);
  }, [entity, period]);
  useEffect(() => { load(); }, [load]);

  async function setExcluded(r, excluded) {
    if (excluded && !reason.trim()) return flash('Give a reason to exclude a receipt.', true);
    const { error } = await supabase.from('qbo_cash_receipts').update({
      excluded, excluded_reason: excluded ? reason.trim() : null, excluded_by: excluded ? profile.id : null
    }).eq('id', r.id);
    if (error) return flash(error.message, true);
    setReasonFor(null); setReason(''); load();
    flash('Saved. Click "Refresh from QuickBooks" to recalculate the month.');
  }
  if (!rows) return <div style={{ fontSize: '12px', color: '#94A3B8' }}>Loading receipts…</div>;
  if (!rows.length) return <div style={{ fontSize: '12px', color: '#94A3B8' }}>No QuickBooks receipts for this month.</div>;
  return (
    <div style={{ background: '#F8FAFC', borderRadius: '8px', padding: '10px' }}>
      <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '6px' }}>
        Payments + sales receipts − refunds. Exclude anything that is not customer revenue (loans, transfers between entities).
      </div>
      <table className="data-table">
        <thead><tr><th>Date</th><th>Type</th><th>Customer</th><th style={{ textAlign: 'right' }}>Amount</th><th></th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} style={r.excluded ? { opacity: 0.55 } : null}>
              <td>{fmtDate(r.txn_date)}</td><td>{r.qbo_type.replace('_', ' ')}</td><td>{r.customer_name || '—'}</td>
              <td style={{ textAlign: 'right' }}>{money(r.amount)}</td>
              <td>
                {r.excluded ? (<><span style={{ fontSize: '11px', color: '#B45309' }}>excluded: {r.excluded_reason}</span>{' '}<button className="btn btn-secondary btn-sm" onClick={() => setExcluded(r, false)}>Include</button></>)
                  : reasonFor === r.id ? (<span style={{ display: 'inline-flex', gap: '6px' }}>
                      <input className="field-input" style={{ width: '180px' }} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason" />
                      <button className="btn btn-primary btn-sm" onClick={() => setExcluded(r, true)}>Exclude</button></span>)
                  : <button className="btn btn-secondary btn-sm" onClick={() => { setReasonFor(r.id); setReason(''); }}>Exclude…</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- Invoices
function InvoicesTab({ invoices, profile, reload, flash }) {
  const [form, setForm] = useState({ invoice_id: '', paid_date: today(), amount: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [emails, setEmails] = useState({});
  const loadEmails = useCallback(async () => {
    const { data } = await supabase.from('royalty_invoice_emails').select('invoice_id,status,sent_at,last_error,to_emails').order('created_at', { ascending: false });
    const m = {}; (data || []).forEach(e => { if (!m[e.invoice_id]) m[e.invoice_id] = e; });
    setEmails(m);
  }, []);
  useEffect(() => { loadEmails(); }, [loadEmails, invoices]);
  async function resend(inv) {
    const { error } = await supabase.from('royalty_invoice_emails').update({ status: 'pending' }).eq('invoice_id', inv.id).in('status', ['sent', 'failed', 'skipped']);
    if (error) return flash(error.message, true);
    qboFetch('/royalty/send', { method: 'POST' }).catch(() => {});
    flash('Queued to resend.'); setTimeout(loadEmails, 2500);
  }
  const open = invoices.filter(i => Number(i.balance) > 0);
  const pick = id => { const inv = invoices.find(i => i.id === id); setForm(f => ({ ...f, invoice_id: id, amount: inv ? String(inv.balance) : '' })); };

  async function record() {
    const amt = parseFloat(form.amount);
    if (!form.invoice_id) return flash('Pick an invoice.', true);
    if (isNaN(amt) || amt <= 0) return flash('Enter the amount received.', true);
    setSaving(true);
    const { error } = await supabase.from('royalty_payments').insert({ invoice_id: form.invoice_id, paid_date: form.paid_date, amount: amt, note: form.note.trim() || null, created_by: profile.id });
    setSaving(false);
    if (error) return flash(error.message, true);
    flash('Payment recorded.'); setForm({ invoice_id: '', paid_date: today(), amount: '', note: '' }); reload();
  }
  return (
    <div>
      <Card>
        <CardHeader title="Record a payment received" hint="Payments cannot be edited — they are append-only" />
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ minWidth: '260px', flex: 1 }}><Field label="Invoice">
            <Select value={form.invoice_id} onChange={e => pick(e.target.value)} placeholder="Select open invoice"
              options={open.map(i => ({ value: i.id, label: `${i.invoice_no} — ${money(i.balance)} due` }))} /></Field></div>
          <div style={{ width: '150px' }}><Field label="Date received"><Input type="date" value={form.paid_date} onChange={e => setForm(f => ({ ...f, paid_date: e.target.value }))} /></Field></div>
          <div style={{ width: '140px' }}><Field label="Amount"><Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></Field></div>
          <div style={{ minWidth: '160px', flex: 1 }}><Field label="Note"><Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="ACH ref, etc." /></Field></div>
          <SubmitBtn onClick={record} loading={saving}>Record</SubmitBtn>
        </div>
      </Card>
      <Card>
        <CardHeader title="Royalty invoices" hint={invoices.length + ' invoices'} />
        {!invoices.length ? <EmptyState text="No royalty invoices yet." /> : (
          <table className="data-table">
            <thead><tr><th>Invoice</th><th>Entity</th><th>Dated</th><th>Due</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>Balance</th><th>Status</th><th>Email</th></tr></thead>
            <tbody>{invoices.map(i => (
              <tr key={i.id}>
                <td>{i.invoice_no}</td><td>{ENTITY_LABELS[i.entity]}</td><td>{fmtDate(i.invoice_date)}</td><td>{fmtDate(i.due_date)}</td>
                <td style={{ textAlign: 'right' }}>{money(i.amount)}</td><td style={{ textAlign: 'right' }}>{money(i.balance)}</td>
                <td><Badge variant={STATUS_VARIANT[i.status] || 'gray'}>{i.status}</Badge></td>
                <td style={{ fontSize: '12px' }} title={emails[i.id] && emails[i.id].to_emails ? emails[i.id].to_emails.join(', ') : ''}>
                  {!emails[i.id] ? <span style={{ color: '#94A3B8' }}>none</span> : (
                    <>
                      <Badge variant={emails[i.id].status === 'sent' ? 'green' : emails[i.id].status === 'failed' ? 'red' : 'gray'}>{emails[i.id].status}</Badge>
                      {emails[i.id].last_error && emails[i.id].status !== 'sent' && <div style={{ color: '#B91C1C' }}>{emails[i.id].last_error}</div>}
                      {['sent', 'failed', 'skipped'].includes(emails[i.id].status) && <button className="link-btn" onClick={() => resend(i)} style={{ marginLeft: 6, background: 'none', border: 0, color: '#2563EB', cursor: 'pointer', fontSize: 12 }}>Resend</button>}
                    </>
                  )}
                </td>
              </tr>))}</tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- Participation
function ParticipationTab({ accruals, summary, payments, profile, reload, flash }) {
  const [form, setForm] = useState({ paid_date: today(), amount: '', note: '' });
  const [saving, setSaving] = useState(false);
  async function record() {
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0) return flash('Enter the amount paid.', true);
    setSaving(true);
    const { error } = await supabase.from('participation_payments').insert({ paid_date: form.paid_date, amount: amt, note: form.note.trim() || null, created_by: profile.id });
    setSaving(false);
    if (error) return flash(error.message, true);
    flash('Payment recorded.'); setForm({ paid_date: today(), amount: '', note: '' }); reload();
  }
  return (
    <div>
      <div className="metrics-grid">
        <div className="metric-card"><div className="metric-label">Total accrued</div><div className="metric-value blue">{money(summary && summary.total_accrued)}</div></div>
        <div className="metric-card"><div className="metric-label">Paid to date</div><div className="metric-value green">{money(summary && summary.total_paid)}</div></div>
        <div className="metric-card"><div className="metric-label">Outstanding</div><div className="metric-value red">{money(summary && summary.outstanding)}</div></div>
      </div>
      <Card>
        <CardHeader title="Record a payment to Wesley" hint="Paid at manager discretion · append-only" />
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ width: '150px' }}><Field label="Date paid"><Input type="date" value={form.paid_date} onChange={e => setForm(f => ({ ...f, paid_date: e.target.value }))} /></Field></div>
          <div style={{ width: '140px' }}><Field label="Amount"><Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></Field></div>
          <div style={{ minWidth: '200px', flex: 1 }}><Field label="Note"><Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} /></Field></div>
          <SubmitBtn onClick={record} loading={saving}>Record</SubmitBtn>
        </div>
      </Card>
      <Card>
        <CardHeader title="Accruals (royalties received)" hint="Accrues when AO collects the royalty, not when it is invoiced" />
        {!accruals.length ? <EmptyState text="Nothing accrued yet." /> : (
          <table className="data-table">
            <thead><tr><th>Received</th><th>Invoice</th><th>Entity</th><th style={{ textAlign: 'right' }}>Royalty received</th><th style={{ textAlign: 'right' }}>Wesley accrual</th></tr></thead>
            <tbody>{accruals.map((a, i) => (<tr key={i}><td>{fmtDate(a.received_date)}</td><td>{a.invoice_no}</td><td>{ENTITY_LABELS[a.entity]}</td><td style={{ textAlign: 'right' }}>{money(a.royalty_received)}</td><td style={{ textAlign: 'right' }}>{money(a.accrual)}</td></tr>))}</tbody>
          </table>
        )}
      </Card>
      <ManagerComp profile={profile} reload={reload} flash={flash} />
      {payments.length > 0 && (
        <Card>
          <CardHeader title="Payments to Wesley" />
          <table className="data-table"><thead><tr><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th><th>Note</th></tr></thead>
            <tbody>{payments.map(p => (<tr key={p.id}><td>{fmtDate(p.paid_date)}</td><td style={{ textAlign: 'right' }}>{money(p.amount)}</td><td>{p.note || '—'}</td></tr>))}</tbody></table>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Founders
function FoundersTab({ rows }) {
  return (
    <Card>
      <CardHeader title="Founder's royalty — quarterly" hint="Due within 30 days of quarter end · confirmed months only" />
      {!rows.length ? <EmptyState text="No confirmed months yet." /> : (
        <table className="data-table">
          <thead><tr><th>Quarter</th><th>Due</th><th style={{ textAlign: 'right' }}>Jacob gross (2%)</th><th style={{ textAlign: 'right' }}>Wesley participation</th><th style={{ textAlign: 'right' }}>Jacob net</th><th style={{ textAlign: 'right' }}>FWNJ cash</th><th style={{ textAlign: 'right' }}>FWNJ — Wesley 2%</th></tr></thead>
          <tbody>{rows.map(r => {
            const d = new Date(r.quarter_start + 'T00:00:00');
            return (<tr key={r.quarter_start}>
              <td>Q{Math.floor(d.getMonth() / 3) + 1} {d.getFullYear()}</td><td>{fmtDate(r.due_date)}</td>
              <td style={{ textAlign: 'right' }}>{money(r.jacob_gross)}</td><td style={{ textAlign: 'right' }}>{money(r.wesley_participation)}</td>
              <td style={{ textAlign: 'right' }}>{money(r.jacob_net)}</td><td style={{ textAlign: 'right' }}>{money(r.fwnj_cash)}</td><td style={{ textAlign: 'right' }}>{money(r.fwnj_wesley_royalty)}</td>
            </tr>);
          })}</tbody>
        </table>
      )}
    </Card>
  );
}

function ManagerComp({ profile, reload, flash }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ period: '', amount: '', note: '' });
  const load = useCallback(async () => { const { data } = await supabase.from('ao_manager_comp').select('*').order('period', { ascending: false }); setRows(data || []); }, []);
  useEffect(() => { load(); }, [load]);
  async function add() {
    const amt = parseFloat(form.amount);
    if (!form.period) return flash('Pick the month.', true);
    if (isNaN(amt) || amt < 0) return flash('Enter the amount (0 if none).', true);
    const { error } = await supabase.from('ao_manager_comp').insert({ period: form.period + '-01', amount: amt, note: form.note.trim() || null, created_by: profile.id });
    if (error) return flash(error.message, true);
    flash('Recorded. It appears on Wesley\'s quarterly statement.'); setForm({ period: '', amount: '', note: '' }); load(); reload();
  }
  return (
    <Card>
      <CardHeader title="Manager compensation paid at AO" hint="Required on every quarterly statement (§3.4(b)) · append-only" />
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ width: '160px' }}><Field label="Month"><Input type="month" value={form.period} onChange={e => setForm(f => ({ ...f, period: e.target.value }))} /></Field></div>
        <div style={{ width: '140px' }}><Field label="Amount paid"><Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></Field></div>
        <div style={{ flex: 1, minWidth: '180px' }}><Field label="Note"><Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} /></Field></div>
        <SubmitBtn onClick={add}>Record</SubmitBtn>
      </div>
      {rows.length > 0 && (
        <table className="data-table" style={{ marginTop: '12px' }}><thead><tr><th>Month</th><th style={{ textAlign: 'right' }}>Amount</th><th>Note</th></tr></thead>
          <tbody>{rows.map(r => (<tr key={r.id}><td>{monthLabel(r.period)}</td><td style={{ textAlign: 'right' }}>{money(r.amount)}</td><td>{r.note || '—'}</td></tr>))}</tbody></table>
      )}
    </Card>
  );
}
